import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {
    Permission,
    PermissionAssignment,
    PermissionObjectType,
    PermissionSubjectType,
    User
} from 'smag-commons/dto/api';
import {combineLatest, Observable, of} from 'rxjs';
import {UsersService} from '../../../shared/users.service';
import {AuthorizedObject} from '../../../api/dto';
import {
    ALL_CONNECTOR_PERMISSION_PATTERNS,
    ALL_RESOURCE_PERMISSION_PATTERNS,
    ALL_USER_PERMISSION_PATTERNS,
    allResourcePermissionPatterns,
    can,
    PermissionPattern
} from '../../../shared/permissions';
import {first, map, shareReplay, switchMap} from 'rxjs/operators';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {ConfigType} from 'smag-commons/cfg';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {PermissionsService} from '../../../shared/permissions.service';
import {MatDialog} from '@angular/material/dialog';
import {EntityNicePathPipe} from '../../../shared/shared.pipe';
import {
    PatternPermissionSettings,
    PermissionMode,
    PermissionSettings
} from '../permission-list/permission-list.component';

export interface AssignmentSubject {
    type: PermissionSubjectType;
    id?: string;
    hint?: string;
}

interface ViewSubject {
    type: PermissionSubjectType;
    canReadUser?: boolean;
    user?: User;
}

interface ViewObject {
    type: PermissionObjectType;
    entity?: HierarchyEntity;
    configType?: ConfigType;
    connectorId?: string;
    user?: User;
    canReadUser?: boolean;
}

interface ViewAssignment {
    assignment: PermissionAssignment;
    subject: Observable<ViewSubject>;
    object: Observable<ViewObject>;
    permissionSettings: Observable<PermissionSettings>;
    permissions: Permission[];
    canEditPermissions: boolean;
}

@Component({
    selector: 'app-permission-assignments',
    templateUrl: './permission-assignments.component.html',
    styleUrls: ['./permission-assignments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PermissionAssignmentsComponent implements OnInit, OnChanges {

    private readonly npp: EntityNicePathPipe;
    @ViewChild('deleteAssignment') private deleteAssignmentDialog: TemplateRef<any>;

    @Input() readOnly: boolean;
    @Input() assignments: AuthorizedObject<PermissionAssignment>[];
    @Input() hideSubject: boolean;
    @Input() hideObject: boolean;
    @Input() reactiveSubject: boolean; // whether clicks on a subject are to be detected
    @Output() subjectClick: EventEmitter<AssignmentSubject> = new EventEmitter<AssignmentSubject>();

    assignmentsColumns: string[];
    viewAssignments: ViewAssignment[];

    // exported in HTML
    PermissionSubjectType = PermissionSubjectType;
    PermissionObjectType = PermissionObjectType;

    constructor(private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly i18n: I18NService,
                private readonly usersService: UsersService,
                private readonly hierarchyService: HierarchyService,
                private readonly permissionsService: PermissionsService,
                private readonly dialog: MatDialog,
                private readonly msgBoxService: MsgBoxService) {
        this.npp = new EntityNicePathPipe();
        this.updateColumns();
        this.updateAssignments();
    }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('hideSubject' in changes || 'hideObject' in changes) {
            this.updateColumns();
            this.changeDetectorRef.markForCheck();
        }
        if ('assignments' in changes) {
            this.updateAssignments();
            this.changeDetectorRef.markForCheck();
        }
    }

    onUpdatePermissions(assignment: ViewAssignment, permissions: Permission[]) {
        this.updateAssignment(assignment.assignment, permissions)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Permissions updated'));
                // keep new permissions
                assignment.permissions = [...permissions];
                this.changeDetectorRef.markForCheck();
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot update permissions'), err);
            });
    }

    onClickRemovePermissions(assignment: ViewAssignment) {
        this.updateAssignment(assignment.assignment, [])
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Permissions updated'));
                // clear permissions
                assignment.permissions = [];
                this.changeDetectorRef.markForCheck();
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot update permissions'), err);
            });
    }

    onClickRemoveAssignment(assignment: ViewAssignment) {
        this.dialog.open(this.deleteAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                assignment
            }
        }).afterClosed().subscribe(del => {
            if (del) {
                this.removeAssignment(assignment.assignment)
                    .then(() => {
                        this.msgBoxService.info(this.i18n.translate('Permissions removed'));
                        // perform array element removal based on reference equality
                        this.assignments = this.assignments.filter(a => a !== assignment.assignment);
                        this.viewAssignments = this.viewAssignments.filter(a => a !== assignment);
                        this.changeDetectorRef.markForCheck();
                    })
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Cannot remove permissions'), err);
                    });
            }
        });
    }

    onClickUsername(subject: ViewSubject, event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        switch (subject.type) {
            case PermissionSubjectType.USER:
                this.subjectClick.emit({
                    type: subject.type,
                    id: subject.user.u3id,
                    hint: this.mkSubjectHint(subject)
                });
                break;
        }
    }

    mkAssignmentRemovalHint(assignment: ViewAssignment): Observable<[string, string]> {
        return combineLatest([assignment.subject, assignment.object]).pipe(
            first(),
            map(([subject, object]) => {
                const subjectStr = this.mkSubjectHint(subject);
                let objectStr = null;
                switch (object.type) {
                    case PermissionObjectType.ENTITY:
                        objectStr = object.entity ?
                            this.npp.transform(object.entity.path, false) :
                            this.i18n.translate('resources');
                        break;
                    case PermissionObjectType.CONNECTOR:
                        objectStr = object.connectorId ?
                            this.i18n.translate('connector {0}', object.connectorId) :
                            this.i18n.translate('connectors');
                        break;
                    case PermissionObjectType.USER:
                        if (object.canReadUser && object.user) {
                            objectStr = this.i18n.translate('user {0}', object.user.username);
                        } else if (object.canReadUser && !object.user) {
                            objectStr = this.i18n.translate('users');
                        } else if (!object.canReadUser) {
                            objectStr = this.i18n.translate('user');
                        }
                        break;
                }
                return [subjectStr, objectStr];
            })
        );
    }

    private mkSubjectHint(subject: ViewSubject): string {
        let subjectStr = null;
        switch (subject.type) {
            case PermissionSubjectType.USER:
                subjectStr = subject.canReadUser ? this.i18n.translate('user {0}', subject.user.username) : this.i18n.translate('user');
                break;
        }
        return subjectStr;
    }

    private updateAssignments() {
        this.viewAssignments = (this.assignments || []).map(a => {
            const object = this.mkViewObject(a.object_type, a.object_id);
            return {
                assignment: a,
                subject: this.mkViewSubject(a.subject_type, a.subject_id),
                object: object,
                permissionSettings: object.pipe(map(object => this.mkPermissionSettings(object))),
                permissions: a.assigned_permissions,
                canEditPermissions: can(a.permissions, Permission.PERMISSIONS)
            };
        });
    }

    private updateColumns() {
        this.assignmentsColumns = [];
        if (!this.hideSubject) {
            this.assignmentsColumns.push('subject');
        }
        if (!this.hideObject) {
            this.assignmentsColumns.push('object');
        }
        this.assignmentsColumns.push('permissions');
    }

    private mkViewSubject(type: PermissionSubjectType, id: string): Observable<ViewSubject> {
        let subject: Observable<ViewSubject> = of({type});
        switch (type) {
            case PermissionSubjectType.USER:
                subject = this.loadUser(id).pipe(
                    map(([user, canReadUser]) => ({
                        type: PermissionSubjectType.USER,
                        user,
                        canReadUser
                    } as ViewSubject))
                );
                break;
        }
        return subject.pipe(
            shareReplay(1)
        );
    }

    private mkViewObject(type: PermissionObjectType, id: string): Observable<ViewObject> {
        let object: Observable<ViewObject> = of({type});
        switch (type) {
            case PermissionObjectType.ENTITY:
                object = id ?
                    combineLatest([this.hierarchyService.hierarchy, this.hierarchyService.hierarchyIds]).pipe(
                        first(),
                        map(([hierarchy, entityIds]) => id !== hierarchy.root ? entityIds[id] : null),
                        map(entity => ({
                            type: PermissionObjectType.ENTITY,
                            entity
                        } as ViewObject))
                    ) :
                    of({
                        type: PermissionObjectType.ENTITY,
                        entity: null
                    } as ViewObject);
                break;
            case PermissionObjectType.CONNECTOR:
                object = of({
                    type: PermissionObjectType.CONNECTOR,
                    connectorId: id // can be `null`
                } as ViewObject);
                break;
            case PermissionObjectType.USER:
                object = id ?
                    this.loadUser(id).pipe(
                        map(([user, canReadUser]) => ({
                            type: PermissionObjectType.USER,
                            user,
                            canReadUser
                        } as ViewObject))
                    ) :
                    of({
                        type: PermissionObjectType.USER,
                        user: null,
                        canReadUser: true
                    } as ViewObject);
                break;
        }
        return object.pipe(
            shareReplay(1)
        );
    }

    private mkPermissionSettings(object: ViewObject): PermissionSettings {
        let permissionPatterns: PermissionPattern[] = [];
        switch (object.type) {
            case PermissionObjectType.ENTITY:
                permissionPatterns = object.entity ? allResourcePermissionPatterns(object.entity) : ALL_RESOURCE_PERMISSION_PATTERNS;
                break;
            case PermissionObjectType.CONNECTOR:
                permissionPatterns = ALL_CONNECTOR_PERMISSION_PATTERNS;
                break;
            case PermissionObjectType.USER:
                permissionPatterns = ALL_USER_PERMISSION_PATTERNS;
                break;
        }
        return {
            mode: PermissionMode.PATTERN,
            allPatterns: permissionPatterns
        } as PatternPermissionSettings;
    }

    private loadUser(u3id: string): Observable<[User, boolean]> {
        return this.usersService.permissions.pipe(
            first(),
            switchMap(usersPerms => can(usersPerms, Permission.READ) ?
                this.usersService.loadUser(u3id).pipe(
                    map(user => [user, true] as [User, boolean])
                ) :
                of([null, false] as [User, boolean])
            )
        );
    }

    private updateAssignment(assignment: PermissionAssignment, newPermissions: Permission[]): Promise<void> {
        return this.permissionsService.assignPermissions({
            subject_type: assignment.subject_type,
            subject_id: assignment.subject_id || null,
            object_type: assignment.object_type,
            object_id: assignment.object_id || null,
            assigned_permissions: newPermissions
        });
    }

    private removeAssignment(assignment: PermissionAssignment): Promise<void> {
        return this.permissionsService.removePermissions(assignment.subject_type,
            assignment.subject_id,
            assignment.object_type,
            assignment.object_id);
    }
}
